import axios, { AxiosInstance } from 'axios';
import {
  AuthResponse,
  ChatFeedbackPayload,
  SignalsResponse,
  QueryResponse,
  Signal,
  SignalFilters,
  User,
  AuditLogsResponse,
  NotificationPreference,
  NotificationRule,
  SignalLogsResponse,
  KpiMetricsResponse,
} from '../types';

import { useAuthStore } from '../stores/authStore';

const API_URL = import.meta.env.VITE_API_URL ?? '';
const base = API_URL ? `${API_URL}/api` : '/api';

const client: AxiosInstance = axios.create({
  baseURL: base,
  timeout: 10000,
});

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout({ sessionExpired: true });
      const event = new CustomEvent('auth:session-expired');
      window.dispatchEvent(event);
    }
    return Promise.reject(error);
  }
);

export const api = {
  auth: {
    login: (email: string, password: string) =>
      client.post<AuthResponse>('/auth/login', { email, password }),
    logout: () => client.post('/auth/logout'),
  },

  signals: {
    list: (page: number = 1, filters?: SignalFilters, limit: number = 20) =>
      client.get<SignalsResponse>('/signals', { params: { page, limit, ...filters } }),
    getById: async (id: string): Promise<Signal> => {
      const res = await client.get<Signal>(`/signals/${id}`);
      return res.data;
    },
    updateTriage: async (id: string, status: Signal['triage_status']): Promise<Signal> => {
      const res = await client.patch<Signal>(`/signals/${id}/triage`, { status });
      return res.data;
    },
    updateNotes: async (id: string, notes: string): Promise<Signal> => {
      const res = await client.patch<Signal>(`/signals/${id}/notes`, { notes });
      return res.data;
    },
    resetDatabase: async (): Promise<{
      message: string;
      deleted: { classifiedDeleted: number; rawDeleted: number };
    }> => {
      const res = await client.delete<{
        message: string;
        deleted: { classifiedDeleted: number; rawDeleted: number };
      }>('/signals/reset');
      return res.data;
    },
    exportSignals: async (filters?: SignalFilters): Promise<Blob> => {
      const res = await client.get('/signals/export', {
        params: filters,
        responseType: 'blob',
      });
      return res.data;
    },
    getLogs: async (id: string): Promise<SignalLogsResponse> => {
      const res = await client.get<SignalLogsResponse>(`/signals/${id}/logs`);
      return res.data;
    },
  },

  ingestion: {
    trigger: async () => {
      const res = await client.post<{
        message: string;
        executionId: string;
        ingestionRunId?: string;
        sources: string[];
        cooldownRemainingSeconds?: number;
        nextAllowedAt?: string;
      }>('/ingestion/trigger');
      return res.data;
    },
    getStatus: async () => {
      const res = await client.get<{
        canTrigger: boolean;
        cooldownRemainingSeconds: number;
        nextAllowedAt: string | null;
        latestRun?: {
          ingestionRunId: string;
          status: 'running' | 'completed' | 'failed';
          newSignalsCount: number;
          startedAt?: string;
          completedAt?: string;
          sources?: string[];
        } | null;
      }>('/ingestion/status');
      return res.data;
    },
  },

  query: Object.assign(
    async (payload: {
      question: string;
      signal_id?: string;
      context?: unknown[];
    }): Promise<QueryResponse> => {
      const res = await client.post<QueryResponse>('/query', payload, { timeout: 90000 });
      return res.data;
    },
    {
      ask: (question: string, signal_id?: string) =>
        client.post<QueryResponse>('/query', { question, signal_id }, { timeout: 90000 }),
      clearHistory: () => client.delete<{ cleared: number }>('/query'),
    }
  ),

  feedback: {
    submit: (payload: ChatFeedbackPayload) =>
      client.post<{ message: string }>('/chat/feedback', payload),
  },

  users: {
    list: async (): Promise<User[]> => {
      const res = await client.get<{ users: User[] }>('/users');
      return res.data.users;
    },
    create: async (data: {
      email: string;
      password: string;
      role: User['role'];
    }): Promise<User> => {
      const res = await client.post<{ message: string; user: User }>('/users', data);
      return res.data.user;
    },
    updateRole: async (id: string, role: User['role']): Promise<User> => {
      const res = await client.patch<{ message: string; user: User }>(`/users/${id}/role`, {
        role,
      });
      return res.data.user;
    },
    delete: async (id: string): Promise<void> => {
      await client.delete(`/users/${id}`);
    },
  },

  audit: {
    list: async (params?: {
      page?: number;
      limit?: number;
      action?: string;
      user_id?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
    }): Promise<AuditLogsResponse> => {
      const res = await client.get<AuditLogsResponse>('/audit-logs', { params });
      return res.data;
    },
  },

  notifications: {
    getPreferences: async (): Promise<NotificationPreference> => {
      const res = await client.get<{ preferences: NotificationPreference }>(
        '/notifications/preferences'
      );
      return res.data.preferences;
    },
    updatePreferences: async (
      data: Partial<NotificationPreference>
    ): Promise<NotificationPreference> => {
      const res = await client.put<{ message: string; preferences: NotificationPreference }>(
        '/notifications/preferences',
        data
      );
      return res.data.preferences;
    },
    listRules: async (): Promise<NotificationRule[]> => {
      const res = await client.get<{ rules: NotificationRule[] }>('/notifications/rules');
      return res.data.rules;
    },
    createRule: async (data: {
      name: string;
      severity_threshold: string;
      recipient_emails: string[];
      enabled?: boolean;
    }): Promise<NotificationRule> => {
      const res = await client.post<{ message: string; rule: NotificationRule }>(
        '/notifications/rules',
        data
      );
      return res.data.rule;
    },
    updateRule: async (
      id: string,
      data: {
        name: string;
        severity_threshold: string;
        recipient_emails: string[];
        enabled?: boolean;
      }
    ): Promise<NotificationRule> => {
      const res = await client.put<{ message: string; rule: NotificationRule }>(
        `/notifications/rules/${id}`,
        data
      );
      return res.data.rule;
    },
    deleteRule: async (id: string): Promise<void> => {
      await client.delete(`/notifications/rules/${id}`);
    },
    sendTestEmail: async (): Promise<{ message: string; sendResult: unknown }> => {
      const res = await client.post<{ message: string; sendResult: unknown }>(
        '/notifications/test-email'
      );
      return res.data;
    },
    triggerDigest: async (
      cadence: 'daily' | 'weekly'
    ): Promise<{ message: string; result: unknown }> => {
      const res = await client.post<{ message: string; result: unknown }>(
        '/notifications/trigger-digest',
        { cadence }
      );
      return res.data;
    },
  },
  analytics: {
    getKpis: async (): Promise<KpiMetricsResponse> => {
      const res = await client.get<KpiMetricsResponse>('/analytics/kpis');
      return res.data;
    },
  },
};

export default api;
